"use client";

import {
  RUNTIME_OFFER_PLACEMENT_HI_RASHI_CALCULATOR_REPORT_BRIDGE,
  RUNTIME_OFFER_PLACEMENT_RASHI_CALCULATOR_REPORT_BRIDGE,
} from "@/lib/offer-placement-runtime-keys.generated";

import dynamic from "next/dynamic";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { generateKundali } from "@/lib/api/kundali";
import type { KundaliRequest, KundaliResponse } from "@/lib/api/kundali";
import BirthDetailsForm from "@/components/BirthDetailsForm";
import CalculatorBrihatReportCTA from "@/components/CalculatorBrihatReportCTA";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import type { RatingPromptProps } from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { reportJsError } from "@/lib/js-error-beacon";

const RASHI_REPORT_OFFER = {
  en: {
    headline: "Your Moon sign is only the surface — what is the full chart hiding?",
    subhead:
      "Your rashi is one graha. Get your full Brihat Kundali to read every graha, dasha, and upay together.",
  },
  hi: {
    headline: "Aapki rashi to bas shuruaat hai — poori kundali kya keh rahi hai?",
    subhead:
      "Rashi sirf ek graha hai. Apni poori Brihat Kundali se sabhi graha, dasha aur upay ek saath samjhein.",
  },
} as const;

const ResultRatingPrompt = dynamic<RatingPromptProps>(() => import("@/components/RatingPrompt"), {
  ssr: false,
  loading: () => null,
});

import { isKnownRashi, resolveRashiInfo, type RashiInfo } from "./rashi-data";
type ExactRashiRequest = KundaliRequest &
  Required<Pick<KundaliRequest, "birth_time" | "latitude" | "longitude">>;

export default function RashiCalculatorClient({
  defaultLanguage = "en",
}: {
  defaultLanguage?: "en" | "hi";
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<KundaliResponse | null>(null);
  // Keep the submitted birth details so the report offer can carry them into
  // /free-kundali (no re-typing — the #1 leak is offer→cart friction).
  const [lastRequest, setLastRequest] = useState<KundaliRequest | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const isHindi = defaultLanguage === "hi";

  useLayoutEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: "auto", block: "start" });
    }
  }, [result]);

  async function handleSubmit(data: KundaliRequest) {
    setLoading(true);
    setError(null);
    try {
      const request = toExactRashiRequest(data);
      const response = await generateKundali({ ...request, skip_interpretation: true });
      setResult(response);
      setLastRequest(request);
      trackCalculatorUsed({
        calculator: "rashi-calculator",
        language: defaultLanguage,
        source_path: sourcePath(defaultLanguage),
        has_time: true,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : isHindi ? "कुछ गड़बड़ हुई" : "Something went wrong"
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="sacred-bg">
      <div className="mx-auto max-w-2xl px-4 pb-12 sm:px-6 sm:pb-16">
        {/* Form */}
        {!result && (
          <div className="mx-auto max-w-md">
            <BirthDetailsForm
              rememberBirthDetails
              onSubmit={handleSubmit}
              loading={loading}
              submitLabel={isHindi ? "मेरी राशि बताएं" : "Find My Rashi"}
              error={error ?? undefined}
              language={defaultLanguage}
              analyticsToolName="rashi-calculator"
              analyticsPlacement="rashi_birth_details"
              requireBirthTime
              requireResolvedPlace
              collectName={false}
              showTrustStrip
            />
          </div>
        )}

        {result && (
          <RashiResultExperience
            result={result}
            resultRef={resultRef}
            lastRequest={lastRequest}
            language={defaultLanguage}
            onReset={() => {
              setResult(null);
              setError(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function toExactRashiRequest(data: KundaliRequest): ExactRashiRequest {
  if (!data.birth_time) {
    throw new Error("Exact birth time is required to calculate Moon sign and nakshatra.");
  }
  if (data.latitude == null || data.longitude == null) {
    throw new Error("Select a birth city from the suggestions to calculate its coordinates.");
  }
  return {
    ...data,
    birth_time: data.birth_time,
    latitude: data.latitude,
    longitude: data.longitude,
  };
}

function RashiResultExperience({
  result,
  resultRef,
  lastRequest,
  language,
  onReset,
}: {
  result: KundaliResponse;
  resultRef: RefObject<HTMLDivElement | null>;
  lastRequest: KundaliRequest | null;
  language: "en" | "hi";
  onReset: () => void;
}) {
  const rashiInfo = resolveRashiInfo(result.moon_rashi);
  // Two DIFFERENT questions that a single nullable lookup used to answer at once:
  //
  //  • ENRICHMENT IS NOT ELIGIBILITY (rule 69, generalized to rendering).
  //    `rashiInfo` supplies cosmetic labels — symbol, ruling planet, element. Its
  //    absence used to `return` early and short-circuit the paid offer, so a
  //    missing decoration withheld monetization on this whole surface.
  //  • AN UNREADABLE ENGINE RESULT IS NOT SELLABLE (rules 29/44 — the engine is
  //    the author of truth). If `moon_rashi` names no rashi at all, we have just
  //    told the seeker we could not read their chart and emitted a contract
  //    error; offering them a ₹199 report built on that same result contradicts
  //    the message and monetizes a known-bad computation.
  //
  // So the offer follows the CONTRACT, never the decorations.
  const engineResultIsReadable = isKnownRashi(result.moon_rashi);
  return (
    <>
      {engineResultIsReadable ? (
        <RashiResult
          result={result}
          rashiInfo={rashiInfo}
          resultRef={resultRef}
          language={language}
        />
      ) : (
        <UnknownRashiResult moonRashi={result.moon_rashi} language={language} onReset={onReset} />
      )}
      {/* Intent-matched offer at the result moment (rules 39/40/46). */}
      {engineResultIsReadable ? (
        <CalculatorBrihatReportCTA
          sourcePath={sourcePath(language)}
          placement={offerPlacement(language)}
          customerProblem="rashi"
          language={language}
          headline={RASHI_REPORT_OFFER[language].headline}
          subhead={RASHI_REPORT_OFFER[language].subhead}
          name={lastRequest?.name}
          birthDate={lastRequest?.birth_date}
          birthTime={lastRequest?.birth_time ?? undefined}
          birthPlace={lastRequest?.birth_place}
        />
      ) : null}
      <button
        type="button"
        onClick={onReset}
        className="mx-auto mt-4 block min-h-11 rounded-lg border border-ish-border px-6 py-3 text-center text-[14px] font-semibold text-ish-text-muted transition-colors hover:text-ish-dark"
      >
        {language === "hi" ? "फिर से गणना करें" : "Calculate Again"}
      </button>
      <div className="mt-6">
        <ResultRatingPrompt
          source={"tool_result" as never}
          mode="thumbs+reasons"
          reasons={RESULT_REASONS}
          prompt="Was this rashi result accurate?"
          context={{
            surface: "rashi_calculator_result",
            path: typeof window !== "undefined" ? window.location.pathname : null,
          }}
        />
      </div>
    </>
  );
}

function UnknownRashiResult({
  moonRashi,
  language,
  onReset,
}: {
  moonRashi?: string;
  language: "en" | "hi";
  onReset: () => void;
}) {
  useEffect(() => {
    reportJsError({
      error_name: "RashiResultContractError",
      error_message: `Unknown moon_rashi returned by Kundali API: ${moonRashi || "missing"}`,
      boundary: "rashi_calculator_result",
    });
  }, [moonRashi]);

  return (
    <div className="card-sacred p-6 text-center">
      <p className="text-sm text-ish-text-muted">
        {language === "hi"
          ? "यह चंद्र राशि परिणाम पढ़ा नहीं जा सका। कृपया फिर से गणना करें।"
          : "We could not read this Moon-sign result. Please calculate again."}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="mt-4 min-h-11 rounded-lg border border-ish-border px-6 py-3 text-sm font-semibold text-ish-text-muted"
      >
        {language === "hi" ? "फिर से गणना करें" : "Calculate Again"}
      </button>
    </div>
  );
}

function RashiResult({
  result,
  rashiInfo,
  resultRef,
  language,
}: {
  result: KundaliResponse;
  rashiInfo?: RashiInfo;
  resultRef: RefObject<HTMLDivElement | null>;
  language: "en" | "hi";
}) {
  const moonPada =
    result.planets.find((planet) => planet.planet_id === "moon")?.nakshatra_pada ?? "-";
  return (
    <div ref={resultRef} className="space-y-6">
      <div className="card-sacred p-6 sm:p-8 text-center">
        <span className="text-5xl block mb-3">{rashiInfo?.symbol ?? "\u2728"}</span>
        <h2
          className="text-[28px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {result.moon_rashi}
        </h2>
        {rashiInfo?.hi ? (
          <p className="text-[18px] mt-1 text-ish-text-muted">{rashiInfo.hi}</p>
        ) : null}
        <div className="divider-warm my-5" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <ResultFact
            label={language === "hi" ? "तत्व" : "Element"}
            value={rashiInfo?.element ?? "-"}
          />
          <ResultFact
            label={language === "hi" ? "स्वामी ग्रह" : "Ruler"}
            value={rashiInfo?.ruler ?? "-"}
          />
          <ResultFact
            label={language === "hi" ? "नक्षत्र" : "Nakshatra"}
            value={result.moon_nakshatra.replace(/_/g, " ")}
            className="capitalize"
          />
          <ResultFact
            label={language === "hi" ? "नक्षत्र पद" : "Nakshatra Pada"}
            value={String(moonPada)}
          />
        </div>
      </div>
    </div>
  );
}

function sourcePath(language: "en" | "hi") {
  return language === "hi" ? "/hi/rashi-calculator" : "/rashi-calculator";
}

function offerPlacement(language: "en" | "hi") {
  return language === "hi"
    ? RUNTIME_OFFER_PLACEMENT_HI_RASHI_CALCULATOR_REPORT_BRIDGE
    : RUNTIME_OFFER_PLACEMENT_RASHI_CALCULATOR_REPORT_BRIDGE;
}

function ResultFact({
  label,
  value,
  className = "",
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div>
      <span className="block text-[12px] mb-1 text-ish-text-faint">{label}</span>
      <span className={`font-semibold text-[14px] text-ish-dark ${className}`}>{value}</span>
    </div>
  );
}
